import React from 'react';
import PropTypes from 'prop-types';
import { List } from 'immutable';
import { Link } from 'react-router-dom';

import DetailsList from '../../../common/DetailsList';
import RecordsAndBadges from '../../../CreditCheck/Components/Report/RecordsAndBadges';
import RoundedButton from '../../../common/RoundedButton';

import { Tenant } from '../../MatchModel';
import { YesNoUnknown } from '../../../common/Models';
import { MinimalRecordDetails } from '../../../CreditCheck/Models';

import tr from '../../../localization/translate';

import './AboutTenantDetails.scss';

function creditCheckContent(asOfferProfile, details, to) {
  if (details === MinimalRecordDetails.empty) {
    return (
      <RoundedButton reverseColors to={to} styleName="credit_check_button">
        { tr('matching.about_tenant.check_credit_button') }
      </RoundedButton>
    );
  }

  if (asOfferProfile && details.checked === YesNoUnknown.NO) {
    return (
      <p className="heavy_text no-entry-gray">
        { tr('matching.about_tenant.credits_not_checked') }
      </p>
    );
  }

  return (<RecordsAndBadges details={details} />);
}

function creditCheckContentWithGenericLink(asOfferProfile, details, to) {
  return (
    <div className="top_margin_24 bottom_margin_24">
      { creditCheckContent(asOfferProfile, details, to) }

      <div className="top_margin_20">
        <Link
          to="/creditCheck"
          target="_blank"
          rel="noopener noreferrer"
          className="effra rem_1 pink_link"
        >
          { tr('matching.about_tenant.check_credit_other_link') }
        </Link>
      </div>
    </div>
  );
}

function formatInterest(tenant) {
  if (tenant.interests.isEmpty()) {
    return '';
  }

  if (tenant.interests.size === 1) {
    return tenant.interests.first();
  }

  const separator = tr('matching.features.list.general_separator');
  const finalSeparator = tr('matching.features.list.final_separator');

  return tenant.interests.slice(0, -1).join(separator) + finalSeparator + tenant.interests.last();
}

function pets(tenant) {
  switch (tenant.has_pets) {
    case YesNoUnknown.YES: return tenant.pet_description;
    case YesNoUnknown.NO: return tr('matching.about_tenant.no_pets');
    default: return '';
  }
}

function smoking(tenant) {
  switch (tenant.smoking) {
    case YesNoUnknown.YES: return tr('common.YES');
    case YesNoUnknown.NO: return tr('common.NO');
    default: return '';
  }
}

function AboutTenantDetails({
  asOfferProfile,
  asSharedProfile,
  checkCreditsLink,
  creditDetails,
  tenant
}) {
  const creditSection = [];

  if (!asSharedProfile || asOfferProfile) {
    creditSection.push({
      title: asOfferProfile ?
        tr('matching.about_tenant.credit_check_no_price') :
        tr('matching.about_tenant.credit_check'),
      content: creditCheckContentWithGenericLink(asOfferProfile, creditDetails, checkCreditsLink),
      id: 'credit_check',
      useDivWrapper: true
    });
  }

  const sections = new List([
    ...creditSection,
    {
      title: tr('matching.about_tenant.occupation'),
      content: tenant.occupation,
      id: 'occupation'
    },
    {
      title: tr('matching.about_tenant.education'),
      content: tenant.education,
      id: 'education'
    },
    {
      title: tr('matching.about_tenant.interests'),
      content: formatInterest(tenant),
      id: 'interests'
    },
    {
      title: tr('matching.about_tenant.pets'),
      content: pets(tenant),
      id: 'pets'
    },
    {
      title: tr('matching.about_tenant.smoking'),
      content: smoking(tenant),
      id: 'smoking'
    },
    {
      title: tr('matching.about_tenant.who'),
      content: tenant.moving_alone ? tr('matching.about_tenant.moving_alone') : tenant.who,
      id: 'who'
    },
    {
      title: tr('matching.about_tenant.why'),
      content: tenant.why,
      id: 'why'
    }
  ]);

  return <DetailsList mobileLikeOnTwoColumn sections={sections} />;
}

AboutTenantDetails.propTypes = {
  asOfferProfile: PropTypes.bool.isRequired,
  asSharedProfile: PropTypes.bool.isRequired,
  creditDetails: MinimalRecordDetails.propType.isRequired,
  checkCreditsLink: PropTypes.string.isRequired,
  tenant: Tenant.propType.isRequired
};

export default AboutTenantDetails;
